import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { audiencesService, type AudiencesListResponse } from '../services';
import type {
  AudienceResponse,
  CreateAudienceRequest,
  UpdateAudienceRequest,
  AudienceQuery,
} from '@troia-v3/types';

export const useAudiences = (
  query?: AudienceQuery
): UseQueryResult<AudiencesListResponse, Error> => {
  return useQuery({
    queryKey: ['audiences', query],
    queryFn: () => audiencesService.getAll(query),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useAudience = (
  id: string | undefined
): UseQueryResult<AudienceResponse, Error> => {
  return useQuery({
    queryKey: ['audience', id],
    queryFn: () => audiencesService.getById(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  });
};

export const useCreateAudience = (): UseMutationResult<
  AudienceResponse,
  Error,
  CreateAudienceRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAudienceRequest) => audiencesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
      queryClient.invalidateQueries({ queryKey: ['audiences-dropdown'] });
    },
  });
};

export const useUpdateAudience = (): UseMutationResult<
  AudienceResponse,
  Error,
  { id: string; data: UpdateAudienceRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => audiencesService.update(id, data),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
      queryClient.invalidateQueries({ queryKey: ['audience', updated.id] });
      queryClient.invalidateQueries({ queryKey: ['audiences-dropdown'] });
    },
  });
};

export const useDeleteAudience = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => audiencesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['audiences'] });
      queryClient.invalidateQueries({ queryKey: ['audiences-dropdown'] });
    },
  });
};
